// generic-client
import leanplum from 'leanplum-sdk';
import { logError, logWarn } from './logger';

// Config phase
const appId = process.env.LEANPLUM_APP_ID;
const leanplumKey = process.env.LEANPLUM_KEY;
const isConfigured = appId && leanplumKey;
const defaultDelay = 30 * 1000;

// setUp
if (isConfigured) {
  leanplum.setAppIdForProductionMode(appId, leanplumKey);
} else {
  logError('Leanplum is misconfigured');
}
class LeanplumUtils {
  static isLeanplumAvailable = false;
  static isWebPushSupported = false;
  static isWebPushSubscribed = false;
  static inbox = undefined;
  static inboxChangeHandlers = [];

  static async initLeanplum() {
    await this.start();
    await this.webpushSetup();
    await this.inboxSetup();
  }

  static start() {
    return new Promise((resolve) => {
      if (!this.isLeanplumAvailable) {
        leanplum.setVariables({});
        leanplum.start((success) => {
          if (!success) logError('Leanplum start did not succeed, check network request logs.');
          this.isLeanplumAvailable = success;
          resolve(success);
        });
      } else {
        resolve(true);
      }
    });
  }

  static inboxSetup() {
    this.inbox = leanplum.inbox();
    this.inbox.changeHandlers = [];
    this.inbox.onChanged(() => {
      this.inboxChangeHandlers.forEach((cb) => cb());
    });
  }

  static async webpushSetup() {
    try {
      if (this.isLeanplumAvailable) {
        this.isWebPushSupported = await leanplum.isWebPushSupported();

        // start Push Notifications
        if (this.isWebPushSupported)
          this.isWebPushSubscribed = await leanplum.isWebPushSubscribed();
      }
    } catch (error) {
      logWarn(`Unable to initialize Leanplum: ${error.message}`);
    }
  }

  static getLeanplumState() {
    return {
      isLeanplumAvailable: this.isLeanplumAvailable,
      isWebPushSupported: this.isWebPushSupported,
      isWebPushSubscribed: this.isWebPushSubscribed,
      inbox: this.inbox,
    };
  }

  static setLeanplumUser(user) {
    if (!user) return;

    // Config phase
    const lpUser = {
      id: user.id,
      first_name: user.firstName,
      last_name: user.lastName,
      email: user.email,
    };

    const currentLUID = leanplum.getUserId();
    if (currentLUID !== lpUser.id) {
      // Update User in Leanplum
      leanplum.setUserAttributes(lpUser.id, lpUser);
    }
    this.fetchNotifications();
  }

  static async toggleWebPushSuscription() {
    if (!this.isLeanplumAvailable) {
      logWarn('Leanplum is not inisitlized');
      return;
    }
    if (!this.isWebPushSupported) {
      logWarn('Leanplum web push is not supported');
      return;
    }

    if (this.isWebPushSubscribed) {
      await leanplum.unregisterFromWebPush();
    } else {
      await leanplum.registerForWebPush('/leanplum.sw.min.js');
    }

    this.isWebPushSubscribed = !this.isWebPushSubscribed;
  }

  static getUnreadMessages() {
    const parsedMessages = this.inbox.unreadMessages().map((message) => {
      return {
        id: message.id(),
        date: new Date(message.timestamp()),
        title: message.title(),
        content: message.subtitle(),
        type: 'leanplum',
        read: message.isRead(),
        to: message.openAction()?.URL || '',
      };
    });
    return parsedMessages;
  }

  static leanplumInboxSubscribe(cb) {
    this.inboxChangeHandlers.push(() => {
      const unreadMessages = this.getUnreadMessages();
      cb(unreadMessages);
    });

    if (!this.interval) {
      this.interval = setInterval(() => {
        this.fetchNotifications();
      }, defaultDelay);
    }
  }

  static fetchNotifications() {
    try {
      this.inbox.downloadMessages();
    } catch (error) {
      logWarn(`Unable to download messages from Leanplum: ${error.message}`);
    }
  }

  static markNotificationAsRead(messageId) {
    this.inbox.markAsRead(messageId);
  }

  static batchNotificationsAsRead() {
    // This number of request works in production env
    const unreadMessages = this.inbox.unreadMessages();
    unreadMessages.map((message) => this.markNotificationAsRead(message.id()));
  }

  static deleteNotification(messageId) {
    this.inbox.remove(messageId);
    this.fetchNotifications();
  }

  static getLeanplumParameters() {
    return {
      deviceId: leanplum._lp._lpRequest.deviceId,
      userId: leanplum.getUserId(),
    };
  }

  static tearDown() {
    // delete message and user ID setting a fake ID and fetchin messages
    clearInterval(this.interval);
    this.interval = undefined;
    this.inboxChangeHandlers = [];
    leanplum.setUserAttributes('none', {});
    this.fetchNotifications();
  }
}

export default LeanplumUtils;
